// src/analytics.js
// Google Analytics 4 tracking utilities for PhoneScope

/**
 * Track page views in GA4
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href
    });
  }
};

/**
 * Track when user compares phones
 * @param {Array} phoneNames - Array of phone names being compared
 */
export const trackPhoneComparison = (phoneNames) => {
  if (window.gtag) {
    window.gtag('event', 'compare_phones', {
      event_category: 'Engagement',
      event_label: phoneNames.join(' vs '),
      phone_count: phoneNames.length
    });
  }
};

/**
 * Track when user adds phone to favorites
 * @param {string} phoneName - Name of the phone
 * @param {string} brand - Phone brand
 */
export const trackFavoriteToggle = (phoneName, brand, action) => {
  if (window.gtag) {
    window.gtag('event', action === 'add' ? 'add_to_favorites' : 'remove_from_favorites', {
      event_category: 'User Interaction',
      event_label: `${brand} ${phoneName}`,
      phone_name: phoneName,
      brand: brand
    });
  }
};

/**
 * Track phone detail views
 * @param {string} phoneName - Name of the phone
 * @param {string} brand - Phone brand
 * @param {number} price - Phone price in selected currency
 */
export const trackPhoneView = (phoneName, brand, price, currency) => {
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      event_category: 'Product',
      currency: currency,
      value: price,
      items: [{
        item_id: phoneName.toLowerCase().replace(/\s+/g, '-'),
        item_name: phoneName,
        item_brand: brand,
        price: price
      }]
    });
  }
};

/**
 * Track search queries
 * @param {string} searchTerm - User's search query
 */
export const trackSearch = (searchTerm) => {
  if (window.gtag) {
    window.gtag('event', 'search', {
      event_category: 'Engagement',
      search_term: searchTerm
    });
  }
};

/**
 * Track filter usage
 * @param {Object} filters - Current filter state
 */
export const trackFilterUsage = (filters) => {
  if (window.gtag) {
    window.gtag('event', 'filter_phones', {
      event_category: 'Engagement',
      filter_brand: filters.brand,
      filter_price: filters.priceRange,
      filter_ram: filters.ram,
      filter_network: filters.network
    });
  }
};

/**
 * Track currency changes
 * @param {string} currency - Selected currency
 */
export const trackCurrencyChange = (currency) => {
  if (window.gtag) {
    window.gtag('event', 'change_currency', {
      event_category: 'User Preference',
      currency: currency
    });
  }
};

/**
 * Track authentication events
 * @param {string} action - 'login' or 'register'
 */
export const trackAuth = (action) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: 'User',
      method: 'email'
    });
  }
};

/**
 * Track AI recommendations
 * @param {string} budget - Budget category
 * @param {string} usage - Usage type
 * @param {number} resultCount - Number of recommendations returned
 */
export const trackRecommendation = (budget, usage, resultCount) => {
  if (window.gtag) {
    window.gtag('event', 'get_recommendations', {
      event_category: 'AI Feature',
      budget: budget,
      usage: usage,
      result_count: resultCount
    });
  }
};

/**
 * Track saved comparisons
 * @param {string} comparisonName - Name of the comparison
 * @param {number} phoneCount - Number of phones in comparison
 */
export const trackSaveComparison = (comparisonName, phoneCount) => {
  if (window.gtag) {
    window.gtag('event', 'save_comparison', {
      event_category: 'User Interaction',
      comparison_name: comparisonName,
      phone_count: phoneCount
    });
  }
};

/**
 * Track tab navigation
 * @param {string} tabName - Name of the tab
 */
export const trackTabChange = (tabName) => {
  if (window.gtag) {
    window.gtag('event', 'tab_navigation', {
      event_category: 'Navigation',
      tab_name: tabName
    });
  }
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 */
export const trackError = (errorType, errorMessage) => {
  if (window.gtag) {
    window.gtag('event', 'exception', {
      description: `${errorType}: ${errorMessage}`,
      fatal: false
    });
  }
};
